import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import toast from 'react-hot-toast';
import { tokens } from '@/theme/tokens';
import useLeaveStore from '@/store/leaveStore';
import useSchoolStore from '@/store/schoolStore';

export function ApplyLeaveModal({ open, onClose, onSuccess }) {
  const { currentSchool } = useSchoolStore();
  const { leaveTypes, fetchLeaveTypes, applyLeave, loading } = useLeaveStore();

  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [halfDaySession, setHalfDaySession] = useState('MORNING');
  const [reason, setReason] = useState('');
  const [attachment, setAttachment] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (open && currentSchool?.id) {
      fetchLeaveTypes(currentSchool.id);
    }
  }, [open, currentSchool?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!leaveTypeId) {
      setErrorMsg('Please select a leave type');
      return;
    }
    if (!fromDate || !toDate) {
      setErrorMsg('Please select from date and to date');
      return;
    }
    setErrorMsg('');

    try {
      await applyLeave({
        leave_type_id: leaveTypeId,
        from_date: fromDate,
        to_date: toDate,
        is_half_day: isHalfDay,
        half_day_session: isHalfDay ? halfDaySession : 'MORNING',
        reason: reason || '',
        attachment: attachment || '',
      });

      toast.success('Leave request submitted successfully');
      if (onSuccess) onSuccess();
      onClose();
      resetForm();
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to submit leave request';
      setErrorMsg(typeof msg === 'string' ? msg : 'Failed to submit leave request');
    }
  };

  const resetForm = () => {
    setLeaveTypeId('');
    setFromDate('');
    setToDate('');
    setIsHalfDay(false);
    setHalfDaySession('MORNING');
    setReason('');
    setAttachment('');
    setErrorMsg('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth paper={{ sx: { borderRadius: tokens.radius.card } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Apply for Leave</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 3 }}>
          {errorMsg && <Alert severity="error">{errorMsg}</Alert>}

          <TextField
            select
            label="Leave Type"
            value={leaveTypeId}
            onChange={(e) => setLeaveTypeId(e.target.value)}
            required
            fullWidth
          >
            {leaveTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name} {type.code ? `(${type.code})` : ''}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              required
              fullWidth
            />
            <TextField
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              required
              fullWidth
            />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={isHalfDay}
                onChange={(e) => setIsHalfDay(e.target.checked)}
                color="primary"
              />
            }
            label="Is Half Day Leave?"
          />

          {isHalfDay && (
            <TextField
              select
              label="Half Day Session"
              value={halfDaySession}
              onChange={(e) => setHalfDaySession(e.target.value)}
              fullWidth
            >
              <MenuItem value="MORNING">Morning Session</MenuItem>
              <MenuItem value="AFTERNOON">Afternoon Session</MenuItem>
            </TextField>
          )}

          <TextField
            label="Reason"
            multiline
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Please specify reason for leave..."
            fullWidth
          />

          <TextField
            label="Attachment URL (Optional)"
            value={attachment}
            onChange={(e) => setAttachment(e.target.value)}
            placeholder="Link to document or medical certificate"
            fullWidth
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={loading} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={loading} startIcon={loading && <CircularProgress size={18} />}>
            {loading ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
